/**
 * AST Validation Schemas
 * Yup schemas for validating AST structure and consistency
 */

import * as yup from 'yup'

import { Block, ContentAST, InlineContent, ValidationError, ValidationResult } from './astTypes'
import { isEmbedReference, isInlineEmbedReference } from './embedReference'

// Short block ID format validation (5-character base36 string)
const blockIdRegex = /^[a-z0-9]{5}$/i
// Encryption ID format: "enc_" + 5-character base36 string
const encryptionIdRegex = /^enc_[a-z0-9]{5}$/i

// Mark validation schemas
const fontMarkSchema = yup.object({
  type: yup.string().oneOf(['font']).required(),
  family: yup.string().oneOf(['Arial', 'Times', 'Helvetica', 'Georgia', 'Verdana']).required(),
})

const colorMarkSchema = yup.object({
  type: yup.string().oneOf(['color']).required(),
  value: yup
    .string()
    .matches(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, 'Invalid color format')
    .required(),
})

const markSchema = yup.lazy((value) => {
  if (typeof value === 'string') {
    return yup.string().oneOf(['bold', 'italic', 'underline'])
  }
  if (typeof value === 'object' && value !== null) {
    if (value.type === 'font') {
      return fontMarkSchema
    }
    if (value.type === 'color') {
      return colorMarkSchema
    }
  }
  return yup.mixed().test('invalid-mark', 'Invalid mark type', () => false)
})

// Text node validation
const textNodeSchema = yup.object({
  type: yup.string().oneOf(['text']).required(),
  text: yup.string().required(),
  marks: yup.array().of(markSchema).optional(),
})

// Encrypted node validation
const encryptedNodeSchema = yup.object({
  type: yup.string().oneOf(['encrypted']).required(),
  id: yup.string().matches(encryptionIdRegex, 'Invalid encryption ID format').required(),
  algo: yup.string().oneOf(['aes-gcm']).required(),
  version: yup.number().oneOf([1]).required(),
  content: yup.array().of(textNodeSchema).min(1).required(),
})

// Link node validation
const linkNodeSchema = yup.object({
  type: yup.string().oneOf(['link']).required(),
  url: yup.string().url('Invalid URL format').required(),
  content: yup.array().of(textNodeSchema).min(1).required(),
})

const inlineEmbedNodeSchema = yup.object({
  type: yup.string().oneOf(['inline-embed']).required(),
  source: yup
    .mixed()
    .required()
    .test('inline-embed-reference', 'Invalid inline embed reference', isInlineEmbedReference),
})

// Inline content validation
const inlineContentSchema = yup.lazy((value) => {
  if (value && value.type === 'text') {
    return textNodeSchema
  }
  if (value && value.type === 'encrypted') {
    return encryptedNodeSchema
  }
  if (value && value.type === 'link') {
    return linkNodeSchema
  }
  if (value && value.type === 'inline-embed') {
    return inlineEmbedNodeSchema
  }
  return yup.mixed().test('invalid-inline-content', 'Invalid inline content type', () => false)
})

// Block attributes validation
const blockAttrsSchema = yup.object({
  align: yup.string().oneOf(['left', 'right', 'center']).optional(),
  dir: yup.string().oneOf(['rtl', 'ltr']).optional(),
})

// List item validation (recursive)
const listItemSchema: yup.ObjectSchema<any> = yup.object({
  id: yup.string().matches(blockIdRegex, 'Invalid block ID format').required(),
  content: yup.array().of(inlineContentSchema).required(),
  children: yup
    .array()
    .of(yup.lazy(() => listItemSchema))
    .max(9, 'List nesting exceeds maximum depth')
    .optional(),
  childrenOrdered: yup.boolean().optional(),
  attrs: blockAttrsSchema.optional(),
})

// Block validation schemas
const headingBlockSchema = yup.object({
  type: yup.string().oneOf(['heading']).required(),
  id: yup.string().matches(blockIdRegex, 'Invalid block ID format').required(),
  level: yup.number().oneOf([1, 2, 3, 4, 5, 6]).required(),
  content: yup.array().of(inlineContentSchema).required(),
  attrs: blockAttrsSchema.optional(),
})

const paragraphBlockSchema = yup.object({
  type: yup.string().oneOf(['paragraph']).required(),
  id: yup.string().matches(blockIdRegex, 'Invalid block ID format').required(),
  content: yup.array().of(inlineContentSchema).required(),
  attrs: blockAttrsSchema.optional(),
})

const listBlockSchema = yup.object({
  type: yup.string().oneOf(['list']).required(),
  id: yup.string().matches(blockIdRegex, 'Invalid block ID format').required(),
  ordered: yup.boolean().required(),
  items: yup.array().of(listItemSchema).min(1).required(),
  attrs: yup
    .object({
      dir: yup.string().oneOf(['rtl', 'ltr']).optional(),
    })
    .optional(),
})

const quoteBlockSchema = yup.object({
  type: yup.string().oneOf(['quote']).required(),
  id: yup.string().matches(blockIdRegex, 'Invalid block ID format').required(),
  content: yup.array().of(inlineContentSchema).required(),
  attrs: blockAttrsSchema.optional(),
})

const dividerBlockSchema = yup.object({
  type: yup.string().oneOf(['divider']).required(),
  id: yup.string().matches(blockIdRegex, 'Invalid block ID format').required(),
  attrs: yup
    .object({
      align: yup.string().oneOf(['left', 'right', 'center']).optional(),
    })
    .optional(),
})

const embedBlockSchema = yup.object({
  type: yup.string().oneOf(['embed']).required(),
  id: yup.string().matches(blockIdRegex, 'Invalid block ID format').required(),
  source: yup.mixed().required().test('embed-reference', 'Invalid embed reference', isEmbedReference),
})

// Block validation (discriminated union)
const blockSchema = yup.lazy((value) => {
  if (!value || typeof value !== 'object') {
    return yup.mixed().test('invalid-block', 'Block must be an object', () => false)
  }

  switch (value.type) {
    case 'heading':
      return headingBlockSchema
    case 'paragraph':
      return paragraphBlockSchema
    case 'list':
      return listBlockSchema
    case 'quote':
      return quoteBlockSchema
    case 'divider':
      return dividerBlockSchema
    case 'embed':
      return embedBlockSchema
    default:
      return yup.mixed().test('invalid-block-type', `Invalid block type: ${value.type}`, () => false)
  }
})

// Root AST validation schema
const contentASTSchema = yup.object({
  version: yup.number().oneOf([1]).required(),
  meta: yup
    .object({
      lang: yup.string().oneOf(['he', 'en', 'mixed']).required(),
      dir: yup.string().oneOf(['rtl', 'ltr']).required(),
      title: yup.string().optional(),
    })
    .required(),
  blocks: yup.array().of(blockSchema).required(),
})

/**
 * Validates an AST structure
 */
export const validate = async (ast: any): Promise<ValidationResult> => {
  try {
    await contentASTSchema.validate(ast, { abortEarly: false })

    // Additional custom validations
    const customErrors = await validateCustomRules(ast)

    return {
      valid: customErrors.length === 0,
      errors: customErrors,
    }
  } catch (error) {
    if (error instanceof yup.ValidationError) {
      const errors: ValidationError[] = error.inner.map((err) => ({
        path: err.path || 'unknown',
        message: err.message,
        code: err.type || 'validation_error',
      }))

      return {
        valid: false,
        errors,
      }
    }

    return {
      valid: false,
      errors: [
        {
          path: 'root',
          message: 'Unknown validation error',
          code: 'unknown_error',
        },
      ],
    }
  }
}

/**
 * Custom validation rules that can't be expressed in Yup
 */
const validateCustomRules = async (ast: ContentAST): Promise<ValidationError[]> => {
  const errors: ValidationError[] = []
  const blockIds = new Set<string>()
  const encryptionIds = new Set<string>()

  // Check for duplicate block IDs
  const checkBlockIds = (blocks: Block[]) => {
    blocks.forEach((block, index) => {
      if (blockIds.has(block.id)) {
        errors.push({
          path: `blocks[${index}].id`,
          message: `Duplicate block ID: ${block.id}`,
          code: 'duplicate_block_id',
        })
      }
      blockIds.add(block.id)

      // Check list items recursively
      if (block.type === 'list') {
        const checkListItems = (items: any[], path: string) => {
          items.forEach((item, itemIndex) => {
            const itemPath = `${path}[${itemIndex}]`
            if (blockIds.has(item.id)) {
              errors.push({
                path: `${itemPath}.id`,
                message: `Duplicate block ID: ${item.id}`,
                code: 'duplicate_block_id',
              })
            }
            blockIds.add(item.id)

            if (item.children) {
              checkListItems(item.children, `${itemPath}.children`)
            }
          })
        }

        checkListItems(block.items, `blocks[${index}].items`)
      }
    })
  }

  // Check for duplicate encryption IDs
  const checkEncryptionIds = (content: InlineContent[], path: string) => {
    content.forEach((node, index) => {
      if (node.type === 'encrypted') {
        if (encryptionIds.has(node.id)) {
          errors.push({
            path: `${path}[${index}].id`,
            message: `Duplicate encryption ID: ${node.id}`,
            code: 'duplicate_encryption_id',
          })
        }
        encryptionIds.add(node.id)

        // Check nested content
        checkEncryptionIds(node.content, `${path}[${index}].content`)
      }
    })
  }

  checkBlockIds(ast.blocks)

  // Check inline content for encryption IDs
  ast.blocks.forEach((block, blockIndex) => {
    if ('content' in block && Array.isArray(block.content)) {
      checkEncryptionIds(block.content, `blocks[${blockIndex}].content`)
    }

    if (block.type === 'list') {
      const checkListItemContent = (items: any[], path: string) => {
        items.forEach((item, itemIndex) => {
          checkEncryptionIds(item.content, `${path}[${itemIndex}].content`)
          if (item.children) {
            checkListItemContent(item.children, `${path}[${itemIndex}].children`)
          }
        })
      }

      checkListItemContent(block.items, `blocks[${blockIndex}].items`)
    }
  })

  return errors
}

/**
 * Validates a single block
 */
export const validateBlock = async (block: any): Promise<ValidationResult> => {
  try {
    await blockSchema.validate(block, { abortEarly: false })
    return { valid: true, errors: [] }
  } catch (error) {
    if (error instanceof yup.ValidationError) {
      const errors: ValidationError[] = error.inner.map((err) => ({
        path: err.path || 'unknown',
        message: err.message,
        code: err.type || 'validation_error',
      }))

      return { valid: false, errors }
    }

    return {
      valid: false,
      errors: [
        {
          path: 'root',
          message: 'Unknown validation error',
          code: 'unknown_error',
        },
      ],
    }
  }
}

/**
 * Validates inline content
 */
export const validateInlineContent = async (content: any): Promise<ValidationResult> => {
  try {
    await yup.array().of(inlineContentSchema).validate(content, { abortEarly: false })
    return { valid: true, errors: [] }
  } catch (error) {
    if (error instanceof yup.ValidationError) {
      const errors: ValidationError[] = error.inner.map((err) => ({
        path: err.path || 'unknown',
        message: err.message,
        code: err.type || 'validation_error',
      }))

      return { valid: false, errors }
    }

    return {
      valid: false,
      errors: [
        {
          path: 'root',
          message: 'Unknown validation error',
          code: 'unknown_error',
        },
      ],
    }
  }
}

// Export schemas for advanced usage
export { blockSchema, contentASTSchema, encryptedNodeSchema, inlineContentSchema, textNodeSchema }
